import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Save, Plus, Trash2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import MediaUploader from '../../components/MediaUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ServiceDetailManager = () => {
  const [services, setServices] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [detailContent, setDetailContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (selectedServiceId) {
      fetchServiceDetail(selectedServiceId);
    }
  }, [selectedServiceId]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/services`);
      setServices(response.data);
      if (response.data.length > 0 && !selectedServiceId) {
        setSelectedServiceId(response.data[0].service_id);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching services:', error);
      setLoading(false);
    }
  };

  const fetchServiceDetail = async (serviceId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/service-details/${serviceId}`);
      setDetailContent(response.data.content);
    } catch (error) {
      console.error('Error fetching service detail:', error);
    }
  };

  const handleSave = async () => {
    if (!selectedServiceId || !detailContent) return;

    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/cms/service-details/${selectedServiceId}`, {
        content: detailContent
      });

      toast({
        title: "Success",
        description: "Service detail page updated successfully"
      });
    } catch (error) {
      console.error('Error updating service detail:', error);
      toast({
        title: "Error",
        description: "Failed to update service detail",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const addBenefit = () => {
    setDetailContent({
      ...detailContent,
      benefits: [...(detailContent.benefits || []), { title: '', description: '', icon: '✓' }]
    });
  };

  const removeBenefit = (index) => {
    const newBenefits = detailContent.benefits.filter((_, i) => i !== index);
    setDetailContent({ ...detailContent, benefits: newBenefits });
  };

  const updateBenefit = (index, field, value) => {
    const newBenefits = [...detailContent.benefits];
    newBenefits[index][field] = value;
    setDetailContent({ ...detailContent, benefits: newBenefits });
  };

  const addProcessStep = () => {
    const stepNumber = (detailContent.process_steps || []).length + 1;
    setDetailContent({
      ...detailContent,
      process_steps: [...(detailContent.process_steps || []), { step: stepNumber.toString(), title: '', description: '' }]
    });
  };

  const removeProcessStep = (index) => {
    const newSteps = detailContent.process_steps.filter((_, i) => i !== index);
    setDetailContent({ ...detailContent, process_steps: newSteps });
  };

  const updateProcessStep = (index, field, value) => {
    const newSteps = [...detailContent.process_steps];
    newSteps[index][field] = value;
    setDetailContent({ ...detailContent, process_steps: newSteps });
  };

  if (loading) {
    return <div className="text-center py-12">Loading services...</div>;
  }

  if (!detailContent) {
    return <div className="text-center py-12">Select a service to edit</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-light text-[#2d2d2d]">Service Detail Pages</h1>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Service Selector */}
      <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
        <label className="block text-sm font-normal text-[#2d2d2d] mb-3">Select Service</label>
        <div className="flex gap-2 flex-wrap">
          {services.map((service) => (
            <Button
              key={service.service_id}
              onClick={() => setSelectedServiceId(service.service_id)}
              variant={selectedServiceId === service.service_id ? 'default' : 'outline'}
              className={selectedServiceId === service.service_id ? 'bg-[#0A1A50]' : ''}
            >
              {service.title}
            </Button>
          ))}
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
        <h2 className="text-2xl font-light text-[#2d2d2d] mb-4">Hero Section</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Hero Title</label>
            <Input
              value={detailContent.hero_title || ''}
              onChange={(e) => setDetailContent({ ...detailContent, hero_title: e.target.value })}
              placeholder="Service Title"
            />
          </div>
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Hero Subtitle</label>
            <Input
              value={detailContent.hero_subtitle || ''}
              onChange={(e) => setDetailContent({ ...detailContent, hero_subtitle: e.target.value })}
              placeholder="Subtitle"
            />
          </div>
          <MediaUploader
            label="Hero Image"
            currentUrl={detailContent.hero_image || ''}
            onMediaSelect={(url) => setDetailContent({ ...detailContent, hero_image: url })}
            accept="image/*"
          />
        </div>
      </div>

      {/* Overview Section */}
      <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
        <h2 className="text-2xl font-light text-[#2d2d2d] mb-4">Overview</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Overview Title</label>
            <Input
              value={detailContent.overview_title || ''}
              onChange={(e) => setDetailContent({ ...detailContent, overview_title: e.target.value })}
              placeholder="Overview"
            />
          </div>
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Overview Content</label>
            <Textarea
              value={detailContent.overview_content || ''}
              onChange={(e) => setDetailContent({ ...detailContent, overview_content: e.target.value })}
              rows={5}
              placeholder="Write detailed overview..."
            />
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-light text-[#2d2d2d]">Benefits</h2>
          <Button onClick={addBenefit} size="sm" className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-1" /> Add Benefit
          </Button>
        </div>
        <div>
          <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Benefits Title</label>
          <Input
            value={detailContent.benefits_title || ''}
            onChange={(e) => setDetailContent({ ...detailContent, benefits_title: e.target.value })}
            placeholder="Key Benefits"
            className="mb-4"
          />
        </div>
        <div className="space-y-4">
          {(detailContent.benefits || []).map((benefit, index) => (
            <div key={index} className="border border-gray-200 p-4 rounded-lg">
              <div className="flex justify-between items-start mb-3">
                <span className="text-sm font-medium text-[#0A1A50]">Benefit {index + 1}</span>
                <Button
                  onClick={() => removeBenefit(index)}
                  size="sm"
                  variant="outline"
                  className="text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input
                  value={benefit.icon || ''}
                  onChange={(e) => updateBenefit(index, 'icon', e.target.value)}
                  placeholder="Icon (emoji or icon class)"
                />
                <Input
                  value={benefit.title || ''}
                  onChange={(e) => updateBenefit(index, 'title', e.target.value)}
                  placeholder="Benefit Title"
                />
                <Input
                  value={benefit.description || ''}
                  onChange={(e) => updateBenefit(index, 'description', e.target.value)}
                  placeholder="Description"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Process Steps Section */}
      <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-light text-[#2d2d2d]">Process Steps</h2>
          <Button onClick={addProcessStep} size="sm" className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-1" /> Add Step
          </Button>
        </div>
        <div>
          <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Process Title</label>
          <Input
            value={detailContent.process_title || ''}
            onChange={(e) => setDetailContent({ ...detailContent, process_title: e.target.value })}
            placeholder="Our Process"
            className="mb-4"
          />
        </div>
        <div className="space-y-4">
          {(detailContent.process_steps || []).map((step, index) => (
            <div key={index} className="border border-gray-200 p-4 rounded-lg">
              <div className="flex justify-between items-start mb-3">
                <span className="text-sm font-medium text-[#0A1A50]">Step {index + 1}</span>
                <Button
                  onClick={() => removeProcessStep(index)}
                  size="sm"
                  variant="outline"
                  className="text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-3">
                <Input
                  value={step.title || ''}
                  onChange={(e) => updateProcessStep(index, 'title', e.target.value)}
                  placeholder="Step Title"
                />
                <Textarea
                  value={step.description || ''}
                  onChange={(e) => updateProcessStep(index, 'description', e.target.value)}
                  placeholder="Step Description"
                  rows={2}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-2xl font-light text-[#2d2d2d] mb-4">Call-to-Action</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">CTA Title</label>
            <Input
              value={detailContent.cta_title || ''}
              onChange={(e) => setDetailContent({ ...detailContent, cta_title: e.target.value })}
              placeholder="Ready to Get Started?"
            />
          </div>
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">CTA Subtitle</label>
            <Input
              value={detailContent.cta_subtitle || ''}
              onChange={(e) => setDetailContent({ ...detailContent, cta_subtitle: e.target.value })}
              placeholder="Contact us to learn more"
            />
          </div>
        </div>
      </div>

      {/* Special Section (Investment Only - Service ID 3) */}
      {selectedServiceId === 3 && (
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h2 className="text-2xl font-light text-[#2d2d2d] mb-4">
            Special Section (Investment Only)
          </h2>
          <p className="text-sm text-[#2d2d2d] mb-4">
            This section appears only on the Investment service page, after the "Our Process" section.
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Section Title</label>
              <Input
                value={detailContent.special_section_title || ''}
                onChange={(e) => setDetailContent({ ...detailContent, special_section_title: e.target.value })}
                placeholder="Explore our land inventory"
              />
            </div>
            <div>
              <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Button Text</label>
              <Input
                value={detailContent.special_section_button_text || ''}
                onChange={(e) => setDetailContent({ ...detailContent, special_section_button_text: e.target.value })}
                placeholder="Access now"
              />
            </div>
            <div>
              <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Button Link</label>
              <Input
                value={detailContent.special_section_button_link || ''}
                onChange={(e) => setDetailContent({ ...detailContent, special_section_button_link: e.target.value })}
                placeholder="https://example.com or /page"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceDetailManager;
